/**
 * Findings triage queue query (pillar A). One shape for `finding.queue`,
 * bulk "select all matching filter" transitions, saved views
 * (`finding_views.query`) and the dashboard's URL state.
 */
import { z } from "zod";
import {
  FINDINGS_PERIOD_BUCKETS,
  FINDINGS_PERIOD_MODES,
  findingsPeriodSchema,
  timeZoneSchema,
} from "./findings-period.js";

export const QUEUE_SYSTEM_VIEWS = [
  "triage",
  "fix_next",
  "awaiting_rerun",
  "regressed",
  "snoozed",
  "closed",
] as const;
export type QueueSystemView = (typeof QUEUE_SYSTEM_VIEWS)[number];

export const QUEUE_SORTS = [
  "priority",
  "last_seen",
  "first_seen",
  "observations",
  "severity",
] as const;
export type QueueSort = (typeof QUEUE_SORTS)[number];

export const QUEUE_GROUPS = [
  "none",
  "capability",
  "outline",
  "scenario",
  "persona",
  "page",
  "state",
] as const;
export type QueueGroup = (typeof QUEUE_GROUPS)[number];

export const QUEUE_TYPE_FILTERS = [
  "problems",
  "all",
  "hinders",
  "missing",
  "incorrect",
  "helps",
  "irrelevant",
] as const;

const QUEUE_STATES = [
  "new",
  "open",
  "in_progress",
  "fix_applied",
  "verified",
  "regressed",
  "snoozed",
  "risk_accepted",
  "dismissed",
] as const;

export const findingQueueQuerySchema = z.object({
  /** System view; its state set applies unless `states` is given. */
  view: z.enum(QUEUE_SYSTEM_VIEWS).optional(),
  type: z.enum(QUEUE_TYPE_FILTERS).optional(),
  states: z.array(z.enum(QUEUE_STATES)).max(9).optional(),
  severities: z
    .array(z.enum(["critical", "major", "minor", "nit"]))
    .max(4)
    .optional(),
  capabilityId: z.string().uuid().optional(),
  /** `primary` = the canonical's primary capability; `any` = any member's. */
  capabilityScope: z.enum(["primary", "any"]).optional(),
  outlineId: z.string().uuid().optional(),
  personaId: z.string().uuid().optional(),
  lens: z.enum(["persona", "domain_expert", "designer"]).optional(),
  judgeCited: z.boolean().optional(),
  /** New in run X: first seen in this walkthrough. */
  firstSeenRunId: z.string().uuid().optional(),
  /** Any member observed in this walkthrough. */
  walkthroughId: z.string().uuid().optional(),
  search: z.string().trim().max(200).optional(),
  /** Hotspot area (pillar B): a grouping key, or `unlinked`. */
  area: z.string().max(2100).optional(),
  /** Pillar D extension points (assignment, tracker links). */
  assignee: z.string().max(64).optional(),
  linked: z.enum(["has", "none"]).optional(),
  request: z.enum(["open"]).optional(),
  sort: z.enum(QUEUE_SORTS).optional(),
  group: z.enum(QUEUE_GROUPS).optional(),
  /** Viewer preference carried by saved views; ignored by the server. */
  density: z.enum(["comfortable", "compact"]).optional(),
  /** Global scope: only canonicals observed in this period. */
  period: findingsPeriodSchema.optional(),
  /** `seen` (default) or `new` — see `FINDINGS_PERIOD_MODES`. */
  periodMode: z.enum(FINDINGS_PERIOD_MODES).optional(),
  /**
   * One bucket of the period summary; with a period it replaces the
   * system view and the period's member condition (queue only).
   */
  periodBucket: z.enum(FINDINGS_PERIOD_BUCKETS).optional(),
  /** Zone the period's calendar dates resolve in (default UTC). */
  tz: timeZoneSchema.optional(),
  /** Global scope: only runs whose build ran against this environment. */
  environment: z.string().min(1).max(256).optional(),
  /** Global scope: only runs whose build is on this git branch. */
  branch: z.string().min(1).max(255).optional(),
  /** Global scope: leave out runs taken against uncommitted changes. */
  committedOnly: z.boolean().optional(),
});
export type FindingQueueQuery = z.infer<typeof findingQueueQuerySchema>;

/**
 * The Findings page's global scope: the part of the query shared by the
 * Queue, Hotspots and Pages views and kept across them.
 */
export const findingsScopeSchema = findingQueueQuerySchema.pick({
  period: true,
  periodMode: true,
  tz: true,
  personaId: true,
  capabilityId: true,
  capabilityScope: true,
  severities: true,
  lens: true,
  environment: true,
  branch: true,
  committedOnly: true,
});
export type FindingsScope = z.infer<typeof findingsScopeSchema>;

/**
 * Saved views (`finding_views.query`) may still carry values this schema
 * dropped: the `overdue` view and filter (the SLA countdown), the `bands`
 * filter and grouping by band. Map them to their nearest meaning so an
 * old view keeps loading. Unknown keys are stripped by the schema itself.
 */
export function normalizeStoredQueueQuery(raw: unknown): unknown {
  if (!raw || typeof raw !== "object") return {};
  const q = { ...(raw as Record<string, unknown>) };
  if (q.view === "overdue") q.view = "fix_next";
  if (q.group === "band") delete q.group;
  return q;
}

/** Default grouping per system view. */
export function defaultGroupForView(view: QueueSystemView | undefined) {
  return view === "awaiting_rerun" ? "scenario" : "none";
}
